// Payroll — three panels. "Timesheets" lists them by status (submitted by
// default, which is the queue) and hands approval to the Approvals screen
// (/approve/:id); an admin can DELETE one from here, which is the
// only place in the rebuild that can. "Paystubs" is GET /api/paystubs with a
// detail sheet showing every line's date, job, client, property and hours, an
// Adjusted pill where a person changed the clock's figure (#507, REBUILD_API
// §0/§3). Generate batch is admin-only and reports the workers whose clock
// time was NOT paid because no approved sheet covers it.
//
// ── Deleting a timesheet is not a small delete ──────────────────────────────
//
// server/routes/timesheets.js DELETE /:id removes the paystubs pointing at the
// sheet, then its entries, then the sheet. So it can destroy a PAID payroll
// record, and the route says nothing about that in its answer. The confirm
// here asks GET /api/timesheets/:id first so it can name the paystub that will
// go with it — and, when that lookup fails, says it could not find out rather
// than implying there was nothing to lose.
import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable, FwButton, FwField, FwInput, FwSelect, FwTextarea, Notice, Pill, Sheet, SectionLabel, formatHours } from '../../../ui/fw';
import { useCan, useRole } from '../../../hooks/usePermission';
import { useAuth, useSettings } from '../../../App';
import { get, post, put, del, serverSentence } from '../../shared/http';
import { fmtMoney, fmtDate, fmtDateRange, fmtDateTime } from '../shared/format';
// Deliberately not dispatch/lib/format.sheetPill: Office does not import from
// another application (the reason serverSentence and pickerNote were moved out
// of apps/dispatch), and a second copy of that mapping here is how the two
// drift apart. The status is printed as the word the server stored.
import { paystubPill, timesheetStagePill, timesheetStageKey, TIMESHEET_STAGE } from '../shared/status';
import { downloadBlob, paystubPdf } from '../shared/download';
import { PageHeader, Refused, LoadFailed, Loading, ActionError, ConfirmSheet } from '../shared/Page';
import { pathFor } from '../shared/moneyEndpoints';
import { PaidDayField, companyToday, companyZone, paidDayBody, paidDayOf, paidDayProblem } from '../shared/paidDay';
// The ONE nav table decides both what /approve is CALLED and whether this
// reader has a row for it — the same authority Overview's stat links consult,
// never a second opinion assembled from role labels here.
import { DESTINATIONS, destinationsFor } from '../../shared/navigation';
// Resolves and formats what a line is priced at. It multiplies nothing and
// sums nothing — see its header, and timesheetPay.test.js, which runs its
// resolver against server/utils/paystubCalc.js over the whole matrix.
import { linePriceLabel, isAmountLine } from '../../../utils/timesheetPay';

/**
 * The status filter, and the request each option makes.
 *
 * `submitted` is first and is the default, so the panel opens on the approval
 * queue and the request it makes is the one this screen has always made.
 * Every value here is one server/database.js stores in timesheets.status
 * (default 'draft'; PUT /:id/approve writes 'approved' or 'rejected').
 */
/** Spacing between paystub PDF downloads. Exported so a test need not wait it out. */
export const EXPORT_GAP_MS = Number(process.env.REACT_APP_EXPORT_GAP_MS) || 400;

export const SHEET_FILTERS = [
  ['recent', 'Last 5 weeks'],
  ['submitted', 'To approve'],
  ['draft', 'Draft'],
  ['approved', 'Approved'],
  ['rejected', 'Rejected'],
  ['all', 'All'],
];

/**
 * ── The DEFAULT is every recent week, not the approval queue. 2026-09-28 ──
 *
 * Owner: "I need a clear view on timesheets for approvals and making sure
 * things got processed properly." The panel opened on `submitted`, so it
 * could answer the first half and never the second: an approved week, and
 * whether its paystub was made and paid, was one filter change away and then
 * mixed with every week there has ever been. The last five weeks, every
 * status, each row saying where it stands (timesheetStagePill), answers both.
 * Five because a pay run is a week or two and a late sheet is rarely older
 * than a month; the other filters are unchanged and one tap away.
 */
export const RECENT_WEEKS = 5;
export const DEFAULT_SHEET_FILTER = 'recent';

/** The first day `recent` asks for: RECENT_WEEKS weeks before `today`, YYYY-MM-DD. */
export function recentSince(today) {
  const [y, m, d] = String(today || '').split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  const at = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  at.setUTCDate(at.getUTCDate() - RECENT_WEEKS * 7);
  return at.toISOString().slice(0, 10);
}

/**
 * The timesheet list, for a status and optionally ONE worker.
 *
 * ── Why the worker is here and not filtered on the client ─────────────────
 *
 * The owner, 2026-09-12: "everything is fucking grouped for all workers..how
 * the fuck do I filter per worker or see everything available for a specific
 * worker?"
 *
 * `GET /api/timesheets` has accepted `user_id` since it was written
 * (routes/timesheets.js reads `const { status, start_date, end_date, user_id }
 * = req.query`) and this screen never sent it. Filtering the rows here instead
 * would look identical on a small team and be wrong on a large one — the list
 * is ordered and truncated by the server, so a client-side filter shows "the
 * matching rows out of the ones that happened to arrive", which is not the
 * same question and quietly answers it wrong.
 */
export const sheetsPath = (status, workerId, since) => {
  const q = [];
  if (status === 'recent') {
    if (since) q.push(`start_date=${encodeURIComponent(since)}`);
  } else if (status !== 'all') q.push(`status=${encodeURIComponent(status)}`);
  if (workerId) q.push(`user_id=${encodeURIComponent(workerId)}`);
  return q.length ? `/timesheets?${q.join('&')}` : '/timesheets';
};

/**
 * The list's one sentence: how many, and how many at each stage — so "two to
 * approve, one approved and not paid" is read off the top of the panel rather
 * than counted down it. Counting rows is not money arithmetic.
 */
export function sheetsSummary(rows, filter, who) {
  const list = Array.isArray(rows) ? rows : [];
  const n = list.length;
  const scope = filter === 'recent' ? ` in the last ${RECENT_WEEKS} weeks` : '';
  const whose = who ? ` for ${who}` : '';
  const head = `${n} timesheet${n === 1 ? '' : 's'}${scope}${whose}`;
  if (!n) return `${head}.`;
  const counts = {};
  for (const t of list) { const k = timesheetStageKey(t); counts[k] = (counts[k] || 0) + 1; }
  const parts = Object.keys(TIMESHEET_STAGE)
    .filter((k) => counts[k])
    .map((k) => `${counts[k]} ${TIMESHEET_STAGE[k].phrase}`);
  return `${head}: ${parts.join(', ')}.`;
}

/** What the list says when it has nothing, in the words of the filter chosen. */
export function sheetsEmpty(filter, who) {
  const label = ((SHEET_FILTERS.find(([v]) => v === filter) || [])[1] || '').toLowerCase();
  if (who) {
    if (filter === 'recent') return `${who} has no timesheets in the last ${RECENT_WEEKS} weeks.`;
    return `${who} has no ${filter === 'all' ? '' : `${label} `}timesheets.`;
  }
  if (filter === 'submitted') return 'Nothing waiting for approval.';
  if (filter === 'recent') return `No timesheets in the last ${RECENT_WEEKS} weeks.`;
  return 'No timesheets with this status.';
}

/** Newest week first, then by name — the order a person reads a run in. */
export function sortSheets(rows) {
  return (Array.isArray(rows) ? rows : []).slice().sort((a, b) => {
    const w = String(b.start_date || '').localeCompare(String(a.start_date || ''));
    return w || String(a.user_name || '').localeCompare(String(b.user_name || ''));
  });
}

/** One worker's paystubs, newest first — the other half of "everything for X". */
export const workerStubsPath = (workerId) => `/paystubs?user_id=${encodeURIComponent(workerId)}`;

/**
 * What the MENU calls /approve, read off navigation.js rather than typed.
 *
 * This screen said "Dispatch" in two places until 2026-09-10 — the header meta
 * and the "No paystubs" empty state. There is no Dispatch: it is a sidebar
 * GROUP on a desktop and nothing at all on a phone, where the screen a person
 * would be hunting for is called Approvals. The same sentence had already been
 * corrected once here ("Open in Dispatch" → "Open the timesheet", 2026-09-05)
 * and these two were missed, which is the argument for reading the name rather
 * than retyping it a third time.
 */
export const APPROVALS_LABEL = (DESTINATIONS.find((d) => d.key === 'approve') || {}).label || 'Approvals';

/**
 * "No paystubs" — a sentence THIS reader can act on.
 *
 * Two halves, and the old one-liner ("Approve a timesheet in Dispatch, or
 * generate a batch above.") got both wrong for most of its readers:
 *
 *   where approval happens   named a thing that is not a screen — and cannot
 *                            simply be renamed, because `accounting` holds
 *                            timesheets ['read','read_all'] and no 'approve'
 *                            (server/database.js), so the Approvals row is not
 *                            in a bookkeeper's menu at all. Telling them to
 *                            approve a week is advice addressed to somebody
 *                            else, which is the /today defect from
 *                            docs/ORIENTATION_2026-09-10.md §5.
 *   "generate a batch"       that button is `isAdmin` below, so a manager and
 *                            a bookkeeper were told to press a control that is
 *                            not on their screen.
 *
 * Pure, and the label is an argument, so the three readers can be asserted
 * without rendering anything and a renamed menu row rewrites the sentence.
 */
export function noPaystubsSentence({ approvalsLabel, canReachApprovals, canGenerate }) {
  const where = canReachApprovals
    ? `Approve a week under ${approvalsLabel}`
    : `A manager approves a week under ${approvalsLabel}`;
  const then = canGenerate ? ', then generate a batch under Make paystubs by hand' : '';
  return `Paystubs are made from approved timesheets. ${where}${then}.`;
}

/**
 * What deleting this sheet will take with it, as a sentence — or null when the
 * lookup has not answered, which the caller must NOT render as "nothing".
 */
export function collateralSentence(detail) {
  if (!detail) return null;
  const stub = detail.paystub;
  if (!stub) return 'No paystub was generated from this sheet, so nothing in payroll points at it.';
  const state = stub.status === 'paid' ? 'already marked PAID' : `marked ${stub.status || 'pending'}`;
  return `Paystub #${stub.id}, ${state}, was generated from this sheet and will be deleted with it.`;
}

export function unapprovedSentence(list) {
  const rows = Array.isArray(list) ? list : [];
  if (!rows.length) return null;
  const n = rows.length;
  const who = rows.map((r) => `${r.name} ${formatHours(r.clock_hours)}h`).join(', ');
  return `${n} worker${n === 1 ? ' has' : 's have'} clock time with no approved sheet — nothing was paid for ${n === 1 ? 'them' : 'them'}: ${who}.`;
}

/**
 * The refusal POST /api/paystubs answers when no approved timesheet covers the
 * period, as one sentence — INCLUDING the clock hours it found and did not pay.
 *
 * That number is the whole value of the 409. "No approved timesheet covers this
 * period" tells the office they cannot create the stub; "and 37.75 hours are
 * sitting on the clock unapproved" tells them what to do about it, which is go
 * and approve the week. Flattening it to the sentence alone throws away the
 * actionable half, and flattening it to "Something went wrong" — which is what
 * describeError does to a 409 — throws away both.
 *
 * Not arithmetic: the figure is printed as the server sent it (paystubs.js
 * rounds it to 2dp before answering). Nothing here adds, multiplies or
 * re-rounds it.
 */
export function noApprovedSheetSentence(err) {
  const body = (err && err.body) || null;
  if (!body || err.status !== 409) return null;
  const said = typeof body.error === 'string' ? body.error : null;
  if (!said) return null;
  const hrs = body.unapproved_clock_hours;
  if (hrs === undefined || hrs === null || Number.isNaN(Number(hrs))) return said;
  return `${said} ${hrs} hour${Number(hrs) === 1 ? '' : 's'} of clock time in this period have not been approved.`;
}

// ── THE REGISTER ────────────────────────────────────────────────────────────
//
// Everything from here to `registerCsvFilename` is pure: it takes what the
// server sent and returns a string. NOTHING below adds, subtracts, multiplies
// or re-rounds a figure — the totals line is `GET /api/paystubs/register`'s
// `totals` object, printed. See docs/PAYROLL_DESIGN.md for why, and
// server/utils/payrollRegister.js for where the arithmetic actually lives.

/** One pay period as a URL-safe key: `2026-08-31_2026-09-06`. */
export function periodKey(period) {
  if (!period || !period.start || !period.end) return '';
  return `${period.start}_${period.end}`;
}

/** The inverse, refusing anything that is not two DATE columns. */
export function parsePeriodKey(key) {
  const [start, end] = String(key || '').split('_');
  const iso = /^\d{4}-\d{2}-\d{2}$/;
  return iso.test(start) && iso.test(end) ? { start, end } : null;
}

/**
 * A period in the picker: the week, then how many people and what is left to
 * pay. "Aug 31 – Sep 6, 2026 · 3 workers · 3 to pay".
 *
 * The counts are the server's integers, printed. A run with nothing left to
 * pay says so rather than showing "0 to pay", because on payroll day the
 * useful distinction is finished / not finished, not a zero.
 */
export function periodOptionLabel(period) {
  if (!period) return '';
  const when = fmtDateRange(period.start, period.end);
  const workers = Number(period.workers) || 0;
  const pending = Number(period.pending) || 0;
  const who = `${workers} worker${workers === 1 ? '' : 's'}`;
  return pending > 0 ? `${when} · ${who} · ${pending} to pay` : `${when} · ${who} · paid`;
}

/**
 * What Create one paystub did, when it paid approved timesheets. Owner,
 * 2026-09-29: "The paystubs come from the fucking timesheets!" So a week with
 * an approved timesheet is paid by THAT sheet's paystub (routes/paystubs.js
 * POST /, `from_timesheets`), never by a second one, and this says which.
 * Figures are the server's, printed and never computed.
 */
export function fromTimesheetsSentence(r) {
  const one = (p) => `#${p.id} for the week of ${fmtDate(p.pay_period_start)}, ${fmtMoney(p.gross_pay)}`;
  const made = Array.isArray(r && r.created) ? r.created : [];
  const had = Array.isArray(r && r.existing) ? r.existing : [];
  const parts = [];
  if (made.length) parts.push(`Made from the approved timesheet${made.length > 1 ? 's' : ''}: paystub ${made.map(one).join('; ')}.`);
  if (had.length) parts.push(`Already has its paystub, so no second one was made: ${had.map(one).join('; ')}.`);
  const failed = Array.isArray(r && r.failed) ? r.failed : [];
  if (failed.length) {
    parts.push(`No paystub could be written for the week of ${failed.map((f) => fmtDate(f.pay_period_start)).join(', ')}. Press Create paystub again, or approve that timesheet again.`);
  }
  return parts.join(' ');
}

/**
 * What a row's RATE cell says — and it must never say "$0.00".
 *
 * `hourly_rate` is a NOT NULL column, and paystubs.js deletes it from every
 * row for a caller without `workers.read_sensitive` (a manager holds
 * `paystubs: ['read']` and not that marker). So an absent rate on this screen
 * always means WITHHELD, never "this person has no rate" — and the register
 * says the word rather than rendering a blank a reader has to interpret or a
 * zero that is a lie about money.
 *
 * `ratesWithheld` comes from the same answer as the rows (`rates_withheld`),
 * so the sentence is the server's own account of what it did, not a guess
 * assembled here from a missing key.
 */
export function rateLabel(row, ratesWithheld) {
  if (ratesWithheld) return 'Rate withheld';
  // ── A stored 0 is NOT a rate. Corrected 2026-09-28 ──────────────────────
  //
  // The header above promised this never says "$0.00", and it did: the
  // owner's "rate displays as 0.00/hr". paystubCalc.computeFigures stores 0
  // as `hourly_rate` whenever ONE rate cannot describe the stub — any line
  // priced flat, or hours paid at two different rates — and also when there
  // was no rate anywhere, which paid nothing. The gross beside it tells the
  // two apart; the drill-in has each line's own rate. (Since 2026-09-29 a week
  // paid wholly at a job's or a line's rate stores THAT rate, not 0 and not
  // the person's own.)
  if (row && row.hourly_rate !== null && row.hourly_rate !== undefined && Number(row.hourly_rate) === 0) {
    return Number(row.gross_pay) > 0 ? 'Per line' : 'No rate';
  }
  const money = fmtMoney(row && row.hourly_rate);
  return money === '—' ? '—' : `${money}/hr`;
}

/**
 * The run, in one sentence: how many people, and how far through paying them.
 *
 * Counts only. The money is in the totals block, where it belongs, and is
 * never restated in prose — two renderings of one figure is how they drift.
 */
export function runSentence(totals) {
  if (!totals) return null;
  if (!totals.rows) return 'No paystubs for this period yet.';
  const w = Number(totals.workers) || 0;
  const who = `${w} worker${w === 1 ? '' : 's'}`;
  const rows = Number(totals.rows) || 0;
  const sheets = rows === w ? '' : `, ${rows} paystubs`;
  if (!totals.pending) return `${who}${sheets}. This run is fully paid.`;
  if (!totals.paid) return `${who}${sheets}. None of this run has been paid yet.`;
  return `${who}${sheets}. ${totals.paid} paid, ${totals.pending} still to pay.`;
}

/**
 * A figure the server could not read, said out loud.
 *
 * `totals.unreadable` is non-empty when a stored money column is not a number
 * — which has really happened here: `PUT /api/paystubs/:id` wrote the string
 * "NaN" into net_pay until 2026-09-09. A register whose total silently omits
 * such a row is a register that does not add up, and the reason it does not is
 * invisible. Named rows, named column.
 */
export function unreadableSentence(totals) {
  const bad = (totals && totals.unreadable) || [];
  if (!bad.length) return null;
  const which = bad.map((u) => `#${u.id} (${u.field})`).join(', ');
  return `${bad.length} paystub${bad.length === 1 ? '' : 's'} hold a figure that is not a number, so ${bad.length === 1 ? 'it is' : 'they are'} not in the totals below: ${which}. The totals are short by whatever those rows are worth.`;
}

/** `GET /api/paystubs/register.csv` for one period. */
export function registerCsvPath(period) {
  return period
    ? `/paystubs/register.csv?start=${encodeURIComponent(period.start)}&end=${encodeURIComponent(period.end)}`
    : '/paystubs/register.csv';
}

/** The name the file lands under, so two runs never collide in a downloads folder. */
export function registerCsvFilename(period) {
  return `payroll-register-${(period && period.start) || 'latest'}.csv`;
}

/**
 * What the bulk PDF button IS, spelled out beside it.
 *
 * The owner, on the version of this screen that offered "EXPORT 41 PDFS" as a
 * top-level action: "who in the fuck right fucking mind would ever want to
 * fucking export every fucking pdf for every fucking worker and it just says
 * PDF..WHAT THE FUCK IS THE FUCKING PDF FOR!!!"
 *
 * They are right twice over. It is not the primary action — the primary
 * artefact of a payroll run is the REGISTER, one file, and the per-worker
 * paystub is the WORKER's document, fetched from their row when somebody wants
 * that person's. And a label naming a count and a file type says nothing about
 * what the documents are or whose.
 *
 * So the count and the period live in a sentence next to the control rather
 * than inside its label: at 320px `.fw-btn__label` truncates with an ellipsis
 * (measured — "MARK ALL PENDING PAID (3)" overflowed its own label box by
 * 32px), and a period that gets ellipsised away is worse than one that was
 * never promised. The same sentence is the button's accessible name.
 */
export function bulkPdfSentence(period, count) {
  const n = Number(count) || 0;
  if (!n) return 'There are no paystubs in this period to export.';
  return `${n} paystub PDF${n === 1 ? '' : 's'} — one document per worker for ${fmtDateRange(period && period.start, period && period.end)}, fetched one at a time. This is the paper a worker is handed, not the register.`;
}

function PaystubSheet({ id, onClose, onChanged, isAdmin, canManage, canPdf }) {
  const q = useQuery({ queryKey: ['office', 'paystub', id], queryFn: () => get(`/paystubs/${id}`), enabled: !!id });
  const [deductions, setDeductions] = useState(null);
  const [notes, setNotes] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmUnpaid, setConfirmUnpaid] = useState(false);
  const [unpaidReason, setUnpaidReason] = useState('');
  // The day it was paid (shared/paidDay.js): asked on Mark paid, changeable
  // once paid. It decides which tax year the pay is in — the 1099 totals read it.
  const [paidOpen, setPaidOpen] = useState(false);
  const [paidDay, setPaidDay] = useState('');
  const [redateOpen, setRedateOpen] = useState(false);
  const [redateDay, setRedateDay] = useState('');
  const [redateReason, setRedateReason] = useState('');
  const { settings } = useSettings();
  const companyDay = companyToday(settings);
  const zone = companyZone(settings);
  const qc = useQueryClient();
  const p = q.data;
  // A stub put back to pending says what its paid date WAS, so it can be paid
  // again on that day rather than re-dated to today (routes/paystubs.js GET).
  const wasPaidDay = p && p.status !== 'paid' ? paidDayOf(p.was_paid_at, zone) : null;
  // Pay that went out before its own period began is almost always a mistyped
  // year — the 2025-for-2026 slip this whole date exists to prevent, pointed
  // the other way (review of #719). Said, not refused: an advance is real.
  const beforePeriod = (day) => (p && p.pay_period_start && day && !paidDayProblem(day, companyDay) && day < p.pay_period_start
    ? `That is before this pay period began (${fmtDate(p.pay_period_start)}). Check the year.`
    : null);
  const currentPaidDay = p ? paidDayOf(p.paid_at, zone) : null;

  const run = async (name, fn) => {
    setBusy(name); setError(null);
    try { await fn(); } catch (e) { setError(e); } finally { setBusy(null); }
  };
  const refresh = async () => { await qc.invalidateQueries({ queryKey: ['office', 'paystub', id] }); if (onChanged) await onChanged(); };
  const saveEdits = () => run('save', async () => {
    const body = {};
    if (deductions !== null) body.deductions = Number(deductions) || 0;
    if (notes !== null) body.notes = notes;
    await put(`/paystubs/${id}`, body);
    setDeductions(null); setNotes(null);
    await refresh();
  });
  const openMarkPaid = () => { setPaidDay(companyDay); setPaidOpen(true); };
  // Today sends no day, so the server stamps the moment — what one press did
  // before a day could be picked. The confirm closes whether or not the server
  // agreed, so a refusal lands in this sheet's ActionError, not behind it.
  const markPaid = async () => {
    await run('paid', async () => { await put(`/paystubs/${id}`, { status: 'paid', ...paidDayBody(paidDay, companyDay) }); await refresh(); });
    setPaidOpen(false);
  };
  // Asked first, and with a reason: going back to pending CLEARS paid_at, and
  // that date decides which tax year the pay belongs to. The server refuses a
  // reversal without a why and records the date it cleared in Activity, as an
  // invoice's Reverse payment does (routes/paystubs.js PUT /:id).
  const markPending = async () => {
    await run('pending', async () => {
      await put(`/paystubs/${id}`, { status: 'pending', reason: unpaidReason.trim() });
      setUnpaidReason('');
      await refresh();
    });
    setConfirmUnpaid(false);
  };
  const openRedate = () => { setRedateDay(paidDayOf(p && p.paid_at, zone) || companyDay); setRedateReason(''); setRedateOpen(true); };
  const redate = async () => {
    await run('redate', async () => { await put(`/paystubs/${id}`, { paid_on: redateDay, reason: redateReason.trim() }); await refresh(); });
    setRedateOpen(false);
  };
  const pdf = () => run('pdf', async () => { await downloadBlob(`/time-entries/paystub/${p.user_id}?paystub_id=${id}&approved_only=true`, `paystub-${id}.pdf`); });
  const remove = () => run('delete', async () => { await del(`/paystubs/${id}`); setConfirmDelete(false); if (onChanged) await onChanged(); onClose(); });

  // On a phone this sheet is narrower than 768px, so RecordList paints these
  // lines as cards: the JOB is the headline, the client the line under it, and
  // the Adjusted pill takes the badge slot — "somebody changed the clock's
  // figure" is the one thing on a payroll line nobody may have to scroll to
  // find. Property is table-only; it is the fourth thing you would ask about a
  // line and a fourth fact is what makes a card wrap to death.
  //
  // The alternative was measured rather than argued, at 320px with a real
  // adjusted line: as a 'fact' the marker costs 224px of card and sits THIRD,
  // under the hours; as the badge it costs 247px and is the first thing read.
  // 23px is not worth burying the one line on a paystub somebody changed by
  // hand. The 247 is the badge slot wrapping this cell's explanatory sentence
  // inside its 45% column — a RecordList question (a one-line clamp), not a
  // reason to drop the sentence, which is the evidence for the adjustment.
  //
  // ── Priced, and a line that is an AMOUNT (2026-09-10) ────────────────────
  //
  // The owner: "adjustment that has a set amount and not hourly". Two defects
  // met on this table, and it is the LAST place a person sees a row before the
  // figure is paid:
  //
  //   1. It carried no money at all — Date, Job, Client, Hours. A paystub
  //      whose Gross was $50 higher than its hours could explain had nothing
  //      on screen accounting for the difference, which is CLAUDE.md's
  //      "plausible number nobody catches".
  //   2. A line with no job rendered "— — — 0.00": nameless, and worth
  //      nothing. custom_description is what such a line is CALLED
  //      (hoursModel.applyLineEdits stores it for exactly this), and its value
  //      is its amount.
  //
  // Both figures come from client/src/utils/timesheetPay.js, whose resolver is
  // the mirror of server/utils/paystubCalc.js that timesheetPay.test.js runs
  // over the same matrix. They FORMAT a stored column. Gross, Deductions and
  // Net above stay exactly what the server sent — nothing here sums anything.
  const lineColumns = [
    { key: 'date', label: 'Date', role: 'fact', render: (l) => fmtDate(l.date) },
    { key: 'job_title', label: 'Job', role: 'title', render: (l) => l.job_title || l.custom_description || '—' },
    { key: 'client_name', label: 'Client', role: 'subtitle', render: (l) => l.client_name || '—' },
    { key: 'property_name', label: 'Property', role: 'hidden', render: (l) => l.property_name || '—' },
    // An amount line has no hours — printing formatHours(0) says the row is
    // worth nothing, next to the amount that says it is not.
    { key: 'hours', label: 'Hours', align: 'right', role: 'fact', render: (l) => (isAmountLine(l) ? '—' : formatHours(l.hours)) },
    { key: 'priced', label: 'Priced', align: 'right', role: 'fact', render: (l) => linePriceLabel(l) || '—' },
    {
      key: 'adjusted', label: '', role: 'badge',
      render: (l) => {
        // Amount first: such a line has no clock behind it, so it can never be
        // "adjusted", and saying which KIND of row it is matters more on a
        // payroll table than any other signal it could carry.
        if (isAmountLine(l)) {
          return (
            <span className="of-inline" data-amount-line="true">
              <Pill tone="sched">Amount</Pill>
            </span>
          );
        }
        return l.adjusted ? (
          <span className="of-inline" data-adjusted="true">
            <Pill tone="warn">Adjusted</Pill>
            <span className="of-cell__sub">{l.clock_hours !== null && l.clock_hours !== undefined ? `clock said ${formatHours(l.clock_hours)}h` : ''}{l.adjustment_reason ? ` — ${l.adjustment_reason}` : ''}{l.adjusted_by_name ? ` (${l.adjusted_by_name})` : ''}</span>
          </span>
        ) : null;
      },
    },
  ];

  const dirty = deductions !== null || notes !== null;
  return (
    <Sheet open={!!id} title={p ? `${p.worker_name} — ${fmtDateRange(p.pay_period_start, p.pay_period_end)}` : 'Paystub'} onClose={onClose} className="of-sheet--wide">
      {q.isPending ? <Loading lines={5} /> : null}
      {q.isError ? <LoadFailed error={q.error} onRetry={() => q.refetch()} what="this paystub" /> : null}
      {p ? (
        <div className="of-stack">
          <div className="of-inline">
            <Pill tone={paystubPill(p).tone} data-status={p.status}>{paystubPill(p).label}</Pill>
            {p.paid_at ? <span className="of-muted">Paid {fmtDateTime(p.paid_at)}</span> : null}
            {p.timesheet_id ? <Link className="of-link" to={`/approve/${p.timesheet_id}`}>Timesheet #{p.timesheet_id}</Link> : <span className="of-muted">No timesheet linked (entered by hand)</span>}
          </div>
          <dl className="of-facts">
            <div><dt>Hours</dt><dd className="fw-num">{formatHours(p.hours_worked)}</dd></div>
            <div><dt>Rate</dt><dd className="fw-num">{p.hourly_rate !== undefined && p.hourly_rate !== null ? rateLabel(p, false) : '—'}</dd></div>
            <div><dt>Gross</dt><dd className="fw-num">{fmtMoney(p.gross_pay)}</dd></div>
            <div><dt>Deductions</dt><dd className="fw-num">{fmtMoney(p.deductions || 0)}</dd></div>
            <div><dt>Net</dt><dd className="fw-num">{fmtMoney(p.net_pay)}</dd></div>
          </dl>
          <ActionError error={error} onDismiss={() => setError(null)} />
          <SectionLabel>Lines</SectionLabel>
          {p.entries && p.entries.length
            ? <DataTable columns={lineColumns} rows={p.entries} rowKey={(l, i) => l.id || i} dense caption="Paystub lines" className="of-paystub-lines" />
            : <p className="of-muted">No timesheet lines — this paystub was entered by hand.</p>}
          {canManage ? (
            <>
              <SectionLabel>Deductions and notes</SectionLabel>
              <div className="of-editor__two">
                <FwField label="Deductions ($)" hint="The server recomputes net pay from this.">
                  <FwInput type="number" min="0" step="0.01" inputMode="decimal" value={deductions === null ? (p.deductions || 0) : deductions} onChange={(e) => setDeductions(e.target.value)} disabled={p.status === 'paid'} data-field="deductions" />
                </FwField>
                <FwField label="Notes">
                  <FwTextarea rows={2} value={notes === null ? (p.notes || '') : notes} onChange={(e) => setNotes(e.target.value)} data-field="notes" />
                </FwField>
              </div>
              <div className="of-inline">
                <FwButton variant="primary" icon="check" onClick={saveEdits} loading={busy === 'save'} disabled={!dirty || (!!busy && busy !== 'save')}>Save</FwButton>
                {p.status === 'pending' ? <FwButton variant="go" onClick={openMarkPaid} disabled={!!busy} data-action="mark-paid">Mark paid</FwButton> : null}
                {p.status === 'paid' ? <FwButton variant="ghost" icon="calendar" onClick={openRedate} disabled={!!busy} data-action="change-paid-date">Change paid date</FwButton> : null}
                {p.status === 'paid' ? <FwButton variant="ghost" onClick={() => { setUnpaidReason(''); setConfirmUnpaid(true); }} disabled={!!busy} data-action="mark-unpaid">Mark unpaid</FwButton> : null}
                {canPdf
                  ? <FwButton variant="ghost" icon="download" onClick={pdf} loading={busy === 'pdf'} disabled={!!busy && busy !== 'pdf'}>Download PDF</FwButton>
                  : <p className="of-muted" data-note="no-pdf">Only an admin or a manager can download somebody else&rsquo;s paystub PDF.</p>}
                {isAdmin ? <FwButton variant="stop" icon="trash" onClick={() => setConfirmDelete(true)} disabled={!!busy}>Delete</FwButton> : null}
              </div>
            </>
          ) : (
            <div className="of-inline">
              {canPdf
                ? <FwButton variant="ghost" icon="download" onClick={pdf} loading={busy === 'pdf'}>Download PDF</FwButton>
                : <p className="of-muted" data-note="no-pdf">Only an admin or a manager can download somebody else&rsquo;s paystub PDF.</p>}
            </div>
          )}
        </div>
      ) : null}
      <ConfirmSheet
        open={paidOpen}
        title="Mark this paystub paid?"
        onClose={() => setPaidOpen(false)}
        onConfirm={markPaid}
        confirmLabel="Mark paid"
        tone="go"
        busy={busy === 'paid'}
        confirmDisabled={!!paidDayProblem(paidDay, companyDay)}
      >
        <PaidDayField
          id="of-stub-paid-on"
          value={paidDay}
          onChange={setPaidDay}
          today={companyDay}
          hint="Decides which year this pay counts in on the 1099s and Money in & out. If it went out before today, pick that day."
        />
        {beforePeriod(paidDay) ? <Notice tone="warn" data-paid-before-period>{beforePeriod(paidDay)}</Notice> : null}
        {wasPaidDay && wasPaidDay !== paidDay ? (
          <Notice tone="info" data-was-paid={wasPaidDay}>
            It was marked paid on {fmtDate(wasPaidDay)} before it was put back to pending.
            {' '}<FwButton variant="ghost" onClick={() => setPaidDay(wasPaidDay)} data-action="use-was-paid">Use {fmtDate(wasPaidDay)}</FwButton>
          </Notice>
        ) : null}
      </ConfirmSheet>
      <ConfirmSheet
        open={redateOpen}
        title="Change the paid date?"
        onClose={() => setRedateOpen(false)}
        onConfirm={redate}
        confirmLabel="Change date"
        busy={busy === 'redate'}
        confirmDisabled={!!paidDayProblem(redateDay, companyDay) || redateDay === currentPaidDay}
      >
        <PaidDayField id="of-stub-redate-on" value={redateDay} onChange={setRedateDay} today={companyDay} />
        {beforePeriod(redateDay) ? <Notice tone="warn" data-paid-before-period>{beforePeriod(redateDay)}</Notice> : null}
        <FwField label="Why (optional)" htmlFor="of-stub-redate-reason" hint="Recorded in the Activity log with your name, the old day and the new one.">
          <FwInput id="of-stub-redate-reason" value={redateReason} onChange={(e) => setRedateReason(e.target.value)} placeholder="Paid Dec 30, marked late" />
        </FwField>
        <p className="of-muted">
          Only the date changes — the amounts and the status stay as they are.
          {redateDay === currentPaidDay ? ' Pick a different day to change it.' : ''}
        </p>
      </ConfirmSheet>
      <ConfirmSheet
        open={confirmUnpaid}
        title="Mark this paystub unpaid?"
        onClose={() => setConfirmUnpaid(false)}
        onConfirm={markPending}
        confirmLabel="Mark unpaid"
        tone="stop"
        busy={busy === 'pending'}
        confirmDisabled={!unpaidReason.trim()}
      >
        <FwField label="Why is it being marked unpaid" htmlFor="of-stub-unpaid-reason" hint="Recorded in the Activity log with your name." required>
          <FwInput id="of-stub-unpaid-reason" value={unpaidReason} onChange={(e) => setUnpaidReason(e.target.value)} placeholder="Marked the wrong week" data-field="unpaid-reason" />
        </FwField>
        <p>
          It goes back to Pending and its paid date{p && p.paid_at ? <>, <strong>{fmtDateTime(p.paid_at)}</strong>,</> : null} is
          cleared. The Activity log keeps that date with your reason, and marking it paid again offers the same day — so if this pay really went out, leave it paid.
        </p>
      </ConfirmSheet>
      <ConfirmSheet open={confirmDelete} title="Delete this paystub?" onClose={() => setConfirmDelete(false)} onConfirm={remove} confirmLabel="Delete" tone="stop" busy={busy === 'delete'}>
        <p>The paystub row is removed. The timesheet it came from is not touched. This cannot be undone.</p>
      </ConfirmSheet>
    </Sheet>
  );
}

export default function Payroll() {
  const canRead = useCan('paystubs', 'read');
  const canManageAll = useCan('paystubs', 'manage_all');
  const canUpdate = useCan('paystubs', 'update');
  const canManage = canManageAll || canUpdate;
  const role = useRole();
  const { user } = useAuth();
  const isAdmin = role === 'admin';
  // DELETE /api/timesheets/:id refuses INSIDE the handler, not with checkRole —
  // so it never appears in roleGateDivergence.test.js and the obligation to
  // mirror it is the same. Since 2026-09-09 it asks the capability, so this
  // asks the same one: `timesheets.delete`, seeded to admin alone and now
  // grantable, which a role comparison could never see.
  const canDeleteSheet = useCan('timesheets', 'delete');
  /**
   * POST /api/timesheets/from-clock for somebody else: hasSheetOversight
   * (timesheets manage_all or manage_team) or timesheets.approve
   * (routes/timesheets.js). "New timesheet" was offered on `paystubs`
   * permissions instead, so a bookkeeper saw it and got "You can only build
   * your own timesheet" (measured, 2026-09-28). Now it asks the same question.
   */
  const canSheetAll = useCan('timesheets', 'manage_all');
  const canSheetTeam = useCan('timesheets', 'manage_team');
  const canSheetApprove = useCan('timesheets', 'approve');
  const canBuildSheet = canSheetAll || canSheetTeam || canSheetApprove;
  /**
   * Does this reader have an Approvals row at all?
   *
   * The nav table, not a role list retyped here — `roles` ∪ `needs`, and the
   * MERGED permission set rather than the role label, exactly as Overview's
   * stat links ask it. So somebody granted timesheets.approve is told to go
   * and use it, and a bookkeeper, who holds neither, is never sent somewhere
   * their menu does not go.
   */
  const canReachApprovals = useMemo(
    () => destinationsFor(role, { permissions: user && user.permissions }).some((d) => d.key === 'approve'),
    [role, user],
  );
  /**
   * POST /api/paystubs — ONE paystub, for one person.
   *
   * This read `useCan('paystubs','create') || role === 'manager'`, mirroring
   * both halves of a route that read, verbatim:
   *   if (!userHasPermission(req, 'paystubs', 'create') && req.user.role !== 'manager')
   *
   * The `|| role === 'manager'` half is gone as of 2026-09-10 because the
   * SERVER's half is: the owner granted managers payroll approval, so the seed
   * carries `paystubs: ['read','create','update','approve']` and the route asks
   * for the capability alone. A manager still sees this button — now because
   * they hold `paystubs.create`, which is also why somebody granted it through
   * the Admin screen now sees it too, and a role comparison never could.
   *
   * The mirror is the point: if this kept the role name after the server
   * dropped it, the two would disagree the moment an admin revoked the
   * capability from the manager role — a button that can only 403.
   */
  const canCreateSingle = useCan('paystubs', 'create');
  /**
   * GET /time-entries/paystub/:userId.
   *
   * This used to be `mayUse('paystubPdf', role)` mirroring an in-handler role
   * check, and it recorded a live bug: accounting holds the full `paystubs`
   * baseline — which is what opens this whole screen — and that route refused
   * them on every row, so "Download PDF" was offered to a bookkeeper and could
   * only ever 403. Invisible, because a 403 on a download looks like a broken
   * file rather than a permission.
   *
   * The route now gates on `paystubs.read`, which accounting holds, so the
   * button works for the person whose job it is — and `canRead` above is
   * already exactly that question.
   */
  const canPdf = canRead;
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  /**
   * WHICH PAY PERIOD, in the URL, so a register is a thing you can link to.
   *
   * `?status=pending` is Overview's "Paystubs pending" card and it still
   * works — but it is resolved by the SERVER into a choice of period (see
   * routes/paystubs.js `periodForStatus`), never into a filter on the rows.
   * A filtered register has a totals line that is the total of a subset, and
   * that is the one thing a totals line may not be. It is forwarded, not
   * interpreted here, so the screen and the route cannot hold two opinions.
   */
  const periodParam = params.get('period') || '';
  const statusHint = params.get('status') || '';
  const [openId, setOpenId] = useState(null);
  const [batchOpen, setBatchOpen] = useState(false);
  // The day the run went out, asked in the batch confirm (shared/paidDay.js).
  const [batchDay, setBatchDay] = useState('');
  const [batchNote, setBatchNote] = useState(null);
  const { settings } = useSettings();
  const companyDay = companyToday(settings);
  const [gen, setGen] = useState({ start: '', end: '' });
  const [genResult, setGenResult] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [sheetStatus, setSheetStatus] = useState(DEFAULT_SHEET_FILTER);
  const sheetsSince = recentSince(companyDay);
  /**
   * WHICH WORKER, in the URL like the period, so "everything for Marcus" is a
   * thing you can link to and come back to.
   *
   * It deliberately does NOT touch the register. A register is the document
   * that goes to the books for a whole period, and `buildRegister` is
   * period-scoped with no worker filter by design — a totals line that is
   * secretly the total of one person is the one thing a totals line may not
   * be. So the worker choice drives the two panels that ARE per-person, and
   * the register says out loud that it is unaffected.
   */
  const workerParam = params.get('worker') || '';
  const setWorker = (id) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    if (id) next.set('worker', id); else next.delete('worker');
    return next;
  }, { replace: true });
  const [newSheet, setNewSheet] = useState(null);   // { user_id, week_start }
  const navigate = useNavigate();

  const [sheetToDelete, setSheetToDelete] = useState(null);
  const [sheetError, setSheetError] = useState(null);
  const [sheetsStale, setSheetsStale] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [singleOpen, setSingleOpen] = useState(false);
  const [single, setSingle] = useState({ user_id: '', start: '', end: '', hours: '', rate: '', deductions: '', notes: '' });
  const [singleError, setSingleError] = useState(null);
  const [singleResult, setSingleResult] = useState(null);

  const sheets = useQuery({
    queryKey: ['office', 'timesheets', sheetStatus, workerParam, sheetStatus === 'recent' ? sheetsSince : null],
    queryFn: () => get(sheetsPath(sheetStatus, workerParam, sheetsSince)),
    enabled: canRead,
  });
  // Opened only by the confirm, because it is the confirm's question: what
  // ELSE does this delete? GET /api/timesheets/:id is the only place that
  // answers it — the list carries no paystub pointer.
  const sheetDetail = useQuery({
    queryKey: ['office', 'timesheet', sheetToDelete && sheetToDelete.id],
    queryFn: () => get(`/timesheets/${sheetToDelete.id}`),
    enabled: !!sheetToDelete,
    staleTime: 0,
  });
  /**
   * The register: the period list, ONE period's rows, and its totals, in one
   * answer.
   *
   * One request and not three, deliberately. The picker, the table and the
   * totals line all describe the same period — and if they came from separate
   * requests they would describe different states of the database for as long
   * as the slowest was in flight. On this screen that means a total over one
   * week under a heading naming another, which is exactly the class of wrong
   * figure CLAUDE.md calls the worst outcome available here.
   */
  const register = useQuery({
    queryKey: ['office', 'register', periodParam, statusHint],
    queryFn: () => {
      const asked = parsePeriodKey(periodParam);
      if (asked) return get(`/paystubs/register?start=${encodeURIComponent(asked.start)}&end=${encodeURIComponent(asked.end)}`);
      if (statusHint) return get(`/paystubs/register?status=${encodeURIComponent(statusHint)}`);
      return get('/paystubs/register');
    },
    enabled: canRead,
  });
  // The roster the single-create picker chooses from. GET /api/workers wants
  // `workers.read`, which admin, manager and accounting all hold — i.e.
  // everybody this form is offered to — so it is enabled with the form rather
  // than guessed at. Opened only when the form is, because a screen nobody is
  // creating a paystub on has no use for it.
  //
  // WIDENED 2026-09-12: the note above ("a screen nobody is creating a paystub
  // on has no use for it") stopped being true when the worker picker landed.
  // The picker needs the roster the moment the screen opens, so this is no
  // longer gated on the single-paystub form — one query, one cache key, rather
  // than a second copy fetching the same list.
  const roster = useQuery({
    queryKey: ['office', 'workers', 'roster'],
    queryFn: () => get('/workers'),
    enabled: canRead,
  });
  const people = useMemo(
    () => (Array.isArray(roster.data) ? roster.data : []).slice()
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    [roster.data],
  );
  const chosen = useMemo(
    () => people.find((w) => String(w.id) === String(workerParam)) || null,
    [people, workerParam],
  );

  /**
   * Everything this worker has been PAID. The register answers "what did this
   * PERIOD cost"; this answers "what has this PERSON had", which is the
   * question the owner could not ask at all.
   */
  const workerStubs = useQuery({
    queryKey: ['office', 'payroll', 'worker-stubs', workerParam],
    queryFn: () => get(workerStubsPath(workerParam)),
    enabled: !!workerParam && canRead,
  });
  const workerStubRows = Array.isArray(workerStubs.data) ? workerStubs.data : null;

  /**
   * Build a worker's week from their clock, and open it.
   *
   * The owner, 2026-09-12: "how the fuck does admin/manager create a
   * timesheet!!"
   *
   * They could, and only from Dispatch: /approve lists the week's workers and
   * tapping one with no sheet offers to build it. Nothing said so from here,
   * which is where somebody doing payroll is standing. This is the same
   * endpoint — POST /api/timesheets/from-clock — so there is one way a sheet
   * comes into existence, not two that can disagree.
   *
   * It goes straight to /approve/:id afterwards, because a sheet you cannot
   * see is not worth having made.
   */
  const buildSheet = async () => {
    if (!newSheet || !newSheet.user_id || !newSheet.week_start) return;
    setBusy('new-sheet');
    setError(null);
    try {
      const sheet = await post('/timesheets/from-clock', {
        user_id: Number(newSheet.user_id), week_start: newSheet.week_start,
      });
      qc.invalidateQueries({ queryKey: ['office', 'timesheets'] });
      setNewSheet(null);
      setBusy(null);
      if (sheet && sheet.id) navigate(`/approve/${sheet.id}`);
    } catch (e) {
      // The RAW error, not a sentence. `ActionError` calls serverSentence
      // itself, so handing it a string means serverSentence runs over a string
      // with no body to read and returns "Something went wrong" — the generic
      // it exists to avoid. Every other handler on this screen passes `e`, and
      // this one did not until the test caught it.
      //
      // What that gets right: this endpoint's two refusals are sentences
      // already — "This week is already approved; reject it before changing
      // hours." and a 404 when the person clocked nothing that week.
      setError(e);
      setBusy(null);
    }
  };
  // A 200 carrying something that is not a list is not an empty list. Passing
  // one to DataTable takes the whole screen down with `rows.map is not a
  // function`, which is the hazard rebuildRenderHazards.test.js scans for —
  // and this screen was on its ALLOWED list until the panel above was built.
  // `null` here means "no list", and every count and table below reads these
  // rather than `*.data`.
  const sheetRows = Array.isArray(sheets.data) ? sortSheets(sheets.data) : null;
  const sheetsUnreadable = sheets.data !== undefined && !Array.isArray(sheets.data);
  // The register's three parts, each `null` when the server did not answer
  // with the shape this screen can read — never `[]`, which would render as
  // "there is nothing" over a refusal.
  const reg = register.data && typeof register.data === 'object' ? register.data : null;
  const stubRows = reg && Array.isArray(reg.rows) ? reg.rows : null;
  const periods = reg && Array.isArray(reg.periods) ? reg.periods : [];
  const totals = reg && reg.totals && typeof reg.totals === 'object' ? reg.totals : null;
  const period = reg && reg.period ? reg.period : null;
  const ratesWithheld = !!(reg && reg.rates_withheld);
  const stubsUnreadable = register.data !== undefined && !stubRows;
  /**
   * Every unpaid paystub IN THE PERIOD ON SCREEN.
   *
   * This used to be every pending paystub in the company, because the list it
   * read was `GET /api/paystubs` unfiltered — so "Mark all pending paid (41)"
   * on payroll day also paid a straggler from six weeks ago that somebody had
   * deliberately held back, and said only "41" about it. A run is a period;
   * closing a run closes that period.
   */
  const pendingIds = useMemo(
    () => (stubRows || []).filter((p) => p.status !== 'paid').map((p) => p.id),
    [stubRows]
  );

  if (!canRead) return <Refused what="payroll" />;

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['office', 'register'] });
    await qc.invalidateQueries({ queryKey: ['office', 'paystubs'] });
    await qc.invalidateQueries({ queryKey: ['office', 'overview'] });
  };
  const run = async (name, fn) => {
    setBusy(name); setError(null);
    try { await fn(); } catch (e) { setError(e); } finally { setBusy(null); }
  };
  // The server's own count, said: "2 paystubs marked as paid (1 already paid
  // …)". It counts what the run moved, not what was sent (review of #719),
  // and a count nobody sees is not a fix.
  const batchPaid = () => run('batch', async () => {
    setBatchNote(null);
    const r = await put('/paystubs/batch/mark-paid', { ids: pendingIds, ...paidDayBody(batchDay, companyDay) });
    setBatchOpen(false);
    await refresh();
    setBatchNote((r && r.message) || 'Marked paid.');
  });
  /**
   * The register as ONE file, for the books. The primary export.
   *
   * Server-rendered (`GET /api/paystubs/register.csv`) rather than serialised
   * here, and that is a money decision, not a convenience: the file's last
   * line is the TOTALS line, and a total is arithmetic. Built in this screen
   * it would be a second implementation of the number shown above the table,
   * free to disagree with it. The route builds both from one query, one scrub
   * and one `totalsFor` call — see server/utils/payrollRegister.js.
   */
  const downloadRegister = () => run('csv', async () => {
    await downloadBlob(registerCsvPath(period), registerCsvFilename(period));
  });
  /**
   * Every paystub IN THE PERIOD, one PDF at a time. A SECONDARY action.
   *
   * The owner, on the version of this that sat in the header as "EXPORT 41
   * PDFS": "who in the fuck right fucking mind would ever want to fucking
   * export every fucking pdf for every fucking worker". The answer is almost
   * nobody, almost never — it is the end-of-period paper run, and the thing an
   * office actually hands an accountant is the register above. So it keeps its
   * place, in the secondary row, under a sentence saying what the documents
   * ARE and which period they cover (`bulkPdfSentence`).
   *
   * What it exports is now the PERIOD rather than "whatever the status filter
   * left on screen" — one selection, in one place, and the same one the totals
   * describe.
   *
   * Three things it does deliberately:
   *
   *   SEQUENTIAL, WITH A GAP. /api/ is behind generalLimiter (server.js:241),
   *   and the classic loop already spaced its downloads by 500ms for the same
   *   reason. Firing twelve at once is how a payroll run turns into 429s
   *   halfway through, and the rate limiter has already bitten this app once,
   *   on a badge poll.
   *
   *   ONE FAILURE DOES NOT ABORT THE REST. Payroll day means everybody gets
   *   paid; stopping at worker 4 of 12 with no record of which four arrived is
   *   worse than finishing and naming the ones that did not.
   *
   *   NO ALL-OR-NOTHING ERROR. What comes back is a count and a list of names,
   *   because "Export failed" after eight successful downloads is a lie.
   */
  const exportAll = async () => {
    const rows = stubRows || [];
    if (!rows.length) return;
    setError(null);
    const failed = [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      setExporting({ done: i, total: rows.length });
      const { path, filename } = paystubPdf(row.user_id, row, row.worker_name);
      try {
        // eslint-disable-next-line no-await-in-loop
        await downloadBlob(path, filename);
      } catch (e) {
        failed.push(row.worker_name || `paystub #${row.id}`);
      }
      if (i < rows.length - 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, EXPORT_GAP_MS));
      }
    }
    setExporting({ done: rows.length, total: rows.length, failed: failed.length, names: failed, finished: true });
  };

  const generate = () => run('generate', async () => {
    const r = await post('/paystubs/generate-batch', { pay_period_start: gen.start, pay_period_end: gen.end });
    setGenResult(r);
    await refresh();
  });

  /**
   * Create one paystub.
   *
   * NOT `run()`: the refusal belongs inside this form, beside the button that
   * was pressed, and it must survive `serverSentence`'s flattening. The 409
   * here is the informative one — see noApprovedSheetSentence above.
   *
   * WHAT IS AND IS NOT SENT. The person, the period, and only those optional
   * fields somebody actually typed. `hours_worked` is OMITTED when blank, not
   * sent as 0: absent means "pay the approved timesheets for this period",
   * each through its own paystub (server/routes/paystubs.js POST /,
   * `from_timesheets` — since 2026-09-29, never a second paystub), while 0 is
   * a deliberate zero-hour paystub for a period no timesheet covers. Gross, deductions and net are never sent and never computed here
   * — they are the server's arithmetic (paystubs.js:243-245), and a second
   * copy of a pay calculation in a screen is what CLAUDE.md rule 4 forbids.
   */
  const createSingle = async () => {
    setBusy('single'); setSingleError(null); setSingleResult(null);
    try {
      const body = {
        user_id: Number(single.user_id),
        pay_period_start: single.start,
        pay_period_end: single.end,
      };
      if (String(single.hours).trim() !== '') body.hours_worked = Number(single.hours);
      if (String(single.rate).trim() !== '') body.hourly_rate = Number(single.rate);
      if (String(single.deductions).trim() !== '') body.deductions = Number(single.deductions);
      if (single.notes.trim() !== '') body.notes = single.notes.trim();
      const made = await post('/paystubs', body);
      setSingleResult(made);
      setSingle({ user_id: '', start: '', end: '', hours: '', rate: '', deductions: '', notes: '' });
      await qc.invalidateQueries({ queryKey: ['office', 'paystubs'] });
    } catch (e) {
      setSingleError(noApprovedSheetSentence(e) || serverSentence(e, 'Could not create that paystub.'));
    } finally {
      setBusy(null);
    }
  };

  /**
   * Not `run()`, on purpose, for two reasons.
   *
   * The refusal belongs INSIDE the confirm, next to the button that was just
   * pressed, rather than in the bar at the top of a screen the sheet is
   * covering. And it goes through `serverSentence`, not `describeError`:
   * describeError flattens every 403 to "You do not have permission to do
   * that.", while this route answers "Only admin can delete timesheets" —
   * which names the actual door. That is the distinction http.js records at
   * serverSentence, and it was measured here: the first version of this
   * handler showed the flattened sentence and the test caught it.
   */
  const deleteSheet = async () => {
    setBusy('delete-sheet');
    setSheetError(null);
    setError(null);
    try {
      await del(pathFor('deleteTimesheet', sheetToDelete.id));
    } catch (e) {
      setSheetError(serverSentence(e, 'Could not delete that timesheet. It is still there.'));
      setBusy(null);
      return;
    }
    setSheetToDelete(null);
    // The paystub the route just deleted was on the panel below, so both lists
    // have to be re-read — and the re-read can fail. A silent "done" over a
    // list still showing the deleted row is the failure this flag prevents.
    await qc.invalidateQueries({ queryKey: ['office', 'paystubs'] });
    await qc.invalidateQueries({ queryKey: ['office', 'overview'] });
    const reread = await sheets.refetch();
    setSheetsStale(!!reread.isError);
    setBusy(null);
  };

  // The approval queue as a card on a phone: WHO, the week under it, and the
  // status where it cannot be missed. `submitted_at` is the column that gives
  // way — the week is already the subtitle and the status already says
  // "submitted", so the timestamp is the least of what is here, and a card
  // shows three facts. Giving it up is what keeps BOTH the link to the sheet
  // and (for an admin) the delete on the card: an admin holding a phone keeps
  // every action an admin at a desk has.
  const sheetColumns = [
    { key: 'user_name', label: 'Worker', role: 'title', render: (t) => t.user_name || `User #${t.user_id}` },
    { key: 'week', label: 'Week', role: 'subtitle', render: (t) => fmtDateRange(t.start_date, t.end_date) },
    { key: 'total_hours', label: 'Hours', align: 'right', role: 'fact', render: (t) => formatHours(t.total_hours) },
    // WHERE it stands, as a pill of a word or two — including whether its
    // paystub was made and paid (owner, 2026-09-28: "making sure things got
    // processed properly"). It printed the stored word, "submitted", which is
    // not what the filter above it calls the same state.
    { key: 'status', label: 'Status', role: 'badge', render: (t) => { const st = timesheetStagePill(t); return <Pill tone={st.tone} data-status={t.status || 'draft'} data-stage={timesheetStageKey(t)}>{st.label}</Pill>; } },
    { key: 'submitted_at', label: 'Submitted', role: 'hidden', render: (t) => fmtDateTime(t.submitted_at || t.updated_at) },
    // Only for a reader whose menu has Approvals: /approve/:id is that screen,
    // and a bookkeeper who followed this link got "Not part of your app".
    ...(canReachApprovals ? [{ key: 'open', label: '', role: 'fact', render: (t) => <Link className="of-link" to={`/approve/${t.id}`}>Open the timesheet</Link> }] : []),
    ...(canDeleteSheet ? [{
      key: 'delete',
      label: '',
      role: 'fact',
      render: (t) => (
        <FwButton variant="stop" icon="trash" onClick={() => { setSheetsStale(false); setSheetError(null); setSheetToDelete(t); }} disabled={!!busy} data-action={`delete-sheet-${t.id}`}>Delete</FwButton>
      ),
    }] : []),
  ];
  /**
   * A REGISTER row: the columns a payroll register has had since ledgers were
   * paper — who, what they are paid at, hours, gross, deductions, net, and
   * whether it has gone out.
   *
   * The PERIOD is not a column any more. It is the thing the picker above
   * chose, and printing it 41 times said nothing while eating the one card
   * slot that RATE now has. Rate is what makes a row reconcilable — hours at
   * that rate is the gross beside it — and it was on the row all along
   * (`hourly_rate`, scrubbed per caller) and never shown.
   *
   * On a phone RecordList paints these as cards with three facts. HOURS,
   * GROSS and NET, in that order, because that is the arithmetic of a pay
   * line read left to right. DEDUCTIONS is table-only and is not lost: it is
   * the visible difference between the Gross and Net on the same card, it is
   * a line of its own in the totals block above, and it is spelled out in the
   * detail sheet one tap away. A fourth fact is what makes a card wrap to
   * death (RECORD_FACT_LIMIT, and the 39px-per-fact measurement in
   * RecordList.js).
   */
  const registerColumns = [
    { key: 'worker_name', label: 'Worker', role: 'title', render: (p) => p.worker_name || `User #${p.user_id}` },
    { key: 'hourly_rate', label: 'Rate', role: 'subtitle', render: (p) => rateLabel(p, ratesWithheld) },
    // ── One figure on a phone card, not three. Owner, 2026-09-28 ─────────
    // "The paystubs cards are taking way too much room ... paystubs get auto
    // generated for the most part and rarely done manually." Hours and Gross
    // stay in the TABLE (a wide container) and in the drill-in one tap away;
    // on a card the row says who, the rate, the status and what they take
    // home. Measured at 390px: a card was three fact rows tall.
    { key: 'hours_worked', label: 'Hours', align: 'right', role: 'hidden', render: (p) => formatHours(p.hours_worked) },
    { key: 'gross_pay', label: 'Gross', align: 'right', role: 'hidden', render: (p) => fmtMoney(p.gross_pay) },
    { key: 'deductions', label: 'Deductions', align: 'right', role: 'hidden', render: (p) => fmtMoney(p.deductions || 0) },
    { key: 'net_pay', label: 'Net', align: 'right', role: 'fact', render: (p) => fmtMoney(p.net_pay) },
    { key: 'status', label: 'Status', role: 'badge', render: (p) => { const s = paystubPill(p); return <Pill tone={s.tone} data-status={p.status}>{s.label}</Pill>; } },
  ];
  /**
   * Choosing a period. It goes in the URL so a register is linkable, and it
   * clears `?status=`: that parameter is a HINT about where to land, and once
   * a person has chosen a week themselves it must not keep overriding them on
   * the next render.
   */
  const setPeriod = (key) => {
    // The last run's count belongs to the period it ran in; left up, it would
    // sit above a different week's list (third review of #719).
    setBatchNote(null);
    const next = new URLSearchParams(params);
    next.delete('status');
    if (key) next.set('period', key); else next.delete('period');
    setParams(next, { replace: true });
  };
  const unapprovedText = genResult ? unapprovedSentence(genResult.unapproved) : null;
  const periodLabel = period ? fmtDateRange(period.start, period.end) : null;
  const runText = runSentence(totals);
  const unreadableText = unreadableSentence(totals);
  const bulkText = bulkPdfSentence(period, (stubRows || []).length);

  return (
    <>
      {/* The ONE action that closes a payroll run, and it is scoped to the run
          on screen. Everything else on this screen is an export or an input,
          and neither belongs in a header. */}
      <PageHeader
        title="Payroll"
        meta={periodLabel
          ? `Pay period ${periodLabel}. Every figure is the server's — hours and rates from approved timesheets, totals from the register.`
          : `Timesheets are approved under ${APPROVALS_LABEL}. Paystubs are paid from approved sheets only.`}
        actions={canManage && pendingIds.length ? (
          <FwButton variant="go" icon="check" onClick={() => { setBatchDay(companyDay); setBatchOpen(true); }} disabled={!!busy} data-action="mark-run-paid">
            Mark {pendingIds.length} paid
          </FwButton>
        ) : null}
      />
      <ActionError error={error} onDismiss={() => setError(null)} />
      {batchNote ? <Notice tone="ok" onDismiss={() => setBatchNote(null)} data-batch-note>{batchNote}</Notice> : null}
      {exporting && exporting.finished ? (
        <Notice tone={exporting.failed ? 'warn' : 'ok'} data-export="result">
          {exporting.failed
            ? `${exporting.total - exporting.failed} of ${exporting.total} paystub PDFs downloaded. These did not: ${exporting.names.join(', ')}.`
            : `${exporting.total} paystub PDF${exporting.total === 1 ? '' : 's'} downloaded.`}
        </Notice>
      ) : null}

      {/* ── Timesheets FIRST, and the whole width. Owner, 2026-09-28 ────────
          "We need to see the timesheets and make sure things look good, more
          than the fucking paystubs. paystubs get auto generated for the most
          part and rarely done manually. I need a clear view on timesheets for
          approvals and making sure things got processed properly."

          This reverses docs/PAYROLL_DESIGN.md's order (register first, "the
          INPUTS, below"), by the owner's decision. MEASURED before, on a
          scratch server: at 1440px this panel was a third of the width in the
          row under the register, beside two paystub forms, so its list drew
          as cards on a desktop; at 390px every paystub card came before any
          timesheet. It now opens on every week of the last five with each
          row's stage — to approve, approved and not paid, paid — and the
          register follows it. */}
      <section className="of-card" data-panel="timesheets">
        <div className="of-card__head">
          <SectionLabel>Timesheets</SectionLabel>
          {/* WHO, then WHAT. The worker comes first because it is the
              question that could not be asked at all until now: this list
              was every worker's, always, with only a status to narrow it. */}
          {people.length ? (
            <FwField label="Worker" htmlFor="of-pay-worker">
              <FwSelect
                id="of-pay-worker"
                value={workerParam}
                onChange={(e) => setWorker(e.target.value)}
                data-field="payroll-worker"
              >
                <option value="">Everyone</option>
                {people.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </FwSelect>
            </FwField>
          ) : null}
          <FwField label="Show" htmlFor="of-pay-sheets">
            <FwSelect id="of-pay-sheets" value={sheetStatus} onChange={(e) => { setSheetsStale(false); setSheetStatus(e.target.value); }} data-field="sheet-status">
              {SHEET_FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </FwSelect>
          </FwField>
          {canBuildSheet && people.length ? (
            <FwButton
              variant="ghost"
              icon="plus"
              onClick={() => setNewSheet({ user_id: workerParam || '', week_start: '' })}
              data-action="new-timesheet"
            >
              New timesheet
            </FwButton>
          ) : null}
        </div>
        {/* The count is a claim about the server, so it is only made once the
            server answered. `sheets.data ? … : ''` and not `(sheets.data ||
            []).length`, which would print "0" over a refusal. */}
        <p className="of-muted" data-sheets-count={sheetRows ? String(sheetRows.length) : 'unknown'}>
          {sheetRows ? sheetsSummary(sheetRows, sheetStatus, chosen && chosen.name) : 'Not loaded yet.'}
        </p>
        {sheetsUnreadable ? (
          <Notice tone="warn" data-sheets="unreadable">
            The server answered, but not with a list of timesheets. Nothing below should be read
            as &quot;there are none&quot;.
          </Notice>
        ) : null}
        {sheetsStale ? (
          <Notice tone="warn" onDismiss={() => setSheetsStale(false)} data-sheets="stale">
            The timesheet was deleted, but this list could not be reloaded afterwards, so it may
            still be showing it. Reload the page.
          </Notice>
        ) : null}
        {sheets.isPending ? <Loading lines={3} /> : null}
        {sheets.isError ? <LoadFailed error={sheets.error} onRetry={() => sheets.refetch()} what={sheetStatus === 'all' || sheetStatus === 'recent' ? 'timesheets' : `${sheetStatus} timesheets`} /> : null}
        {sheetRows ? (
          <DataTable
            columns={sheetColumns}
            rows={sheetRows}
            dense
            caption="Timesheets"
            empty={sheetsEmpty(sheetStatus, chosen && chosen.name)}
            className="of-approve"
          />
        ) : null}
      </section>

      {/* ── The register ──────────────────────────────────────────────────
          The period itself: who is paid, how much, and whether it has gone
          out. It was first on the screen until 2026-09-28, when the owner put
          the timesheets above it (see that section). Its totals still head
          it, so "what leaves the bank" is still the first line of the part
          of the screen that answers it. */}
      <section className="of-card of-register" data-panel="register">
        <div className="of-card__head">
          <SectionLabel>Register</SectionLabel>
          <FwField label="Pay period" htmlFor="of-pay-period">
            <FwSelect
              id="of-pay-period"
              value={period ? periodKey(period) : ''}
              onChange={(e) => setPeriod(e.target.value)}
              disabled={!periods.length}
              data-field="pay-period"
            >
              {periods.length
                ? periods.map((p) => <option key={periodKey(p)} value={periodKey(p)}>{periodOptionLabel(p)}</option>)
                : <option value="">No pay periods yet</option>}
              {/* A period reached by link that has no paystubs is still the
                  period on screen, so it must be in the picker — otherwise the
                  control shows a week other than the one the table is
                  describing. */}
              {period && !periods.some((p) => periodKey(p) === periodKey(period))
                ? <option value={periodKey(period)}>{fmtDateRange(period.start, period.end)} · empty</option>
                : null}
            </FwSelect>
          </FwField>
        </div>

        {register.isPending ? <Loading lines={5} /> : null}
        {register.isError ? <LoadFailed error={register.error} onRetry={() => register.refetch()} what="the payroll register" /> : null}
        {stubsUnreadable ? (
          <Notice tone="warn" data-paystubs="unreadable">
            The server answered, but not with a register. Nothing below should be read as
            &quot;there is nothing to pay&quot;.
          </Notice>
        ) : null}

        {/* ── The totals: the employer's number ──────────────────────────
            AT THE TOP, which is not where a paper register puts it, and the
            departure is deliberate. On a phone the bottom of a register is
            after 41 cards, and "what leaves the bank" is the first question
            somebody opens this screen with — it may not be the last thing
            they can reach. On a desktop it reads as the summary line of the
            panel it heads.

            Every figure here is `totals` off the register endpoint, printed.
            Nothing on this screen adds, subtracts or multiplies money; see
            server/utils/payrollRegister.js, which is where the sum happens,
            over the exact rows the table below is showing. */}
        {totals ? (
          <>
            {unreadableText ? (
              <Notice tone="stop" data-totals="unreadable">{unreadableText}</Notice>
            ) : null}
            <dl className="of-facts of-register__totals" data-register-totals={String(totals.rows)}>
              <div><dt>Workers</dt><dd className="fw-num" data-total="workers">{totals.workers}</dd></div>
              <div><dt>Hours</dt><dd className="fw-num" data-total="hours">{formatHours(totals.hours)}</dd></div>
              <div><dt>Gross</dt><dd className="fw-num" data-total="gross">{fmtMoney(totals.gross)}</dd></div>
              <div><dt>Deductions</dt><dd className="fw-num" data-total="deductions">{fmtMoney(totals.deductions)}</dd></div>
              <div className="of-register__net"><dt>Net pay</dt><dd className="fw-num" data-total="net">{fmtMoney(totals.net)}</dd></div>
            </dl>
            <p className="of-muted" data-run-sentence="true">{runText}</p>
            {ratesWithheld ? (
              <p className="of-muted" data-rates="withheld">
                Hourly rates are withheld from your account, so the Rate column says so rather than
                showing a blank. Gross and net are unaffected.
              </p>
            ) : null}
          </>
        ) : null}

        {stubRows ? (
          stubRows.length
            ? <DataTable columns={registerColumns} rows={stubRows} onRowClick={(p) => setOpenId(p.id)} caption={`Payroll register, ${periodLabel}`} className="of-paystubs" />
            : (
              // A sentence, not an EmptyState box: that was ~210px of dashed
              // border and an icon at 390px, ahead of anything a person could
              // act on (owner, 2026-09-28: the paystubs take "way too much
              // room"). Same words.
              <p className="of-muted" data-paystubs-empty="true">
                <strong>No paystubs in this period.</strong>{' '}
                {periods.length
                  ? 'Pick another pay period above, or make this one under Make paystubs by hand below.'
                  : noPaystubsSentence({ approvalsLabel: APPROVALS_LABEL, canReachApprovals, canGenerate: isAdmin })}
              </p>
            )
        ) : null}

        {/* ── The two exports, and what each is FOR ───────────────────────
            The register CSV is ONE file and it is what goes to the books.
            The paystub PDFs are one document per worker and they are what a
            worker is handed. They are different artefacts for different
            people, so they are labelled as such rather than both being
            "export". */}
        {stubRows && stubRows.length && canPdf ? (
          <div className="of-stack" data-exports="true">
            <SectionLabel>Take it away</SectionLabel>
            <div className="of-inline">
              <FwButton
                variant="primary"
                icon="download"
                onClick={downloadRegister}
                loading={busy === 'csv'}
                disabled={!!busy && busy !== 'csv'}
                data-action="export-register"
                aria-label={`Download the payroll register for ${periodLabel} as a CSV`}
              >
                Register CSV
              </FwButton>
              <FwButton
                variant="ghost"
                icon="download"
                onClick={exportAll}
                loading={!!exporting && !exporting.finished}
                disabled={!!busy || (!!exporting && !exporting.finished)}
                data-action="export-paystubs"
                aria-label={bulkText}
              >
                {exporting && !exporting.finished
                  ? `Exporting ${exporting.done + 1} of ${exporting.total}…`
                  : `Paystub PDFs (${stubRows.length})`}
              </FwButton>
            </div>
            <p className="of-muted" data-export-note="register">
              The register CSV is one file for the books: every row above plus the totals line.
            </p>
            <p className="of-muted" data-export-note="pdfs">{bulkText}</p>
          </div>
        ) : null}
      </section>


      {/* ── Everything this PERSON has been paid ──────────────────────────
          The register above answers "what did this period cost". This
          answers "what has this person had", and the two are different
          questions — which is why this is a panel and not a filter on the
          register. Its figures are per-paystub, as the server stored them;
          nothing here is summed, so there is no totals line to be the total
          of a subset. */}
      {chosen ? (
        <section className="of-card" data-panel="worker-paystubs">
          <div className="of-card__head">
            <SectionLabel>{chosen.name} — paystubs</SectionLabel>
            <FwButton variant="quiet" onClick={() => setWorker('')} data-action="clear-worker">
              Show everyone
            </FwButton>
          </div>
          <p className="of-muted" data-worker-stubs-count={workerStubRows ? String(workerStubRows.length) : 'unknown'}>
            {workerStubRows
              ? `${workerStubRows.length} paystub${workerStubRows.length === 1 ? '' : 's'}, every period — not just the one above.`
              : 'Not loaded yet.'}
          </p>
          {workerStubs.isPending ? <Loading lines={3} /> : null}
          {workerStubs.isError ? (
            <LoadFailed error={workerStubs.error} onRetry={() => workerStubs.refetch()} what={`${chosen.name}'s paystubs`} />
          ) : null}
          {workerStubRows ? (
            <DataTable
              columns={registerColumns}
              rows={workerStubRows}
              onRowClick={(row) => setOpenId(row.id)}
              dense
              caption={`Paystubs for ${chosen.name}`}
              empty={`${chosen.name} has no paystubs yet.`}
              className="of-paystubs"
            />
          ) : null}
        </section>
      ) : null}

      {/* ── Making paystubs BY HAND, folded away. Owner, 2026-09-28 ────────
          "paystubs get auto generated for the most part and rarely done
          manually." Approving a week makes its paystub (routes/timesheets.js
          autoGeneratePaystub), so these two forms are the exception — a
          person the batch missed, a period approved late — and they were two
          full cards beside the timesheets, stretched to the height of that
          list. A native <details>: closed by default, one tap to open, and
          every control inside is still the same control, in the page. */}
      {canCreateSingle || isAdmin ? (
        <details className="of-byhand" data-panel="by-hand">
          <summary className="of-byhand__summary" data-action="open-by-hand">Make paystubs by hand</summary>
          {/* One wrapper, and it is load-bearing: see .of-byhand__body. */}
          <div className="of-byhand__body">
          <p className="of-muted">
            Approving a week makes its paystub. These are for the exceptions: a person the
            batch missed, a correction, a period approved late.
          </p>
          <div className="of-panels">
        {canCreateSingle ? (
          <section className="of-byhand__part" data-panel="one-paystub">
            <SectionLabel>One paystub</SectionLabel>
            <p className="of-muted">
              For a person the batch missed — a mid-period hire, a correction, a week
              approved late. Leave the hours blank to pay the approved timesheets for
              the period, or type them to override.
            </p>
            {!singleOpen ? (
              <div className="of-inline">
                <FwButton variant="primary" icon="payroll" onClick={() => { setSingleOpen(true); setSingleError(null); setSingleResult(null); }}>Create one paystub</FwButton>
              </div>
            ) : (
              <div className="of-stack">
                <FwField label="Person">
                  <FwSelect
                    value={single.user_id}
                    onChange={(e) => setSingle((f) => ({ ...f, user_id: e.target.value }))}
                    data-field="single_user_id"
                  >
                    <option value="">Choose a person</option>
                    {(Array.isArray(roster.data) ? roster.data : [])
                      .filter((w) => w.is_active !== 0)
                      .map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </FwSelect>
                </FwField>
                {roster.isError ? <Notice tone="warn">Could not load the crew list, so there is nobody to choose. Reload the screen.</Notice> : null}
                <div className="of-editor__two">
                  <FwField label="Period start">
                    <FwInput type="date" value={single.start} onChange={(e) => setSingle((f) => ({ ...f, start: e.target.value }))} data-field="single_start" />
                  </FwField>
                  <FwField label="Period end">
                    <FwInput type="date" value={single.end} onChange={(e) => setSingle((f) => ({ ...f, end: e.target.value }))} data-field="single_end" />
                  </FwField>
                </div>
                <div className="of-editor__two">
                  <FwField label="Hours" hint="Blank pays the approved timesheets, line by line">
                    <FwInput type="number" step="0.25" min="0" inputMode="decimal" value={single.hours} onChange={(e) => setSingle((f) => ({ ...f, hours: e.target.value }))} data-field="single_hours" />
                  </FwField>
                  <FwField label="Rate" hint="Only with hours, for a period with no timesheet">
                    <FwInput type="number" step="0.01" min="0" inputMode="decimal" value={single.rate} onChange={(e) => setSingle((f) => ({ ...f, rate: e.target.value }))} data-field="single_rate" />
                  </FwField>
                </div>
                <FwField label="Deductions">
                  <FwInput type="number" step="0.01" min="0" inputMode="decimal" value={single.deductions} onChange={(e) => setSingle((f) => ({ ...f, deductions: e.target.value }))} data-field="single_deductions" />
                </FwField>
                <FwField label="Notes">
                  <FwTextarea rows={2} value={single.notes} onChange={(e) => setSingle((f) => ({ ...f, notes: e.target.value }))} data-field="single_notes" />
                </FwField>
                {/* The figures are the server's. Saying so is cheaper than a
                    reader wondering why this form shows no total. */}
                <p className="of-muted">Gross and net are calculated when it is saved.</p>
                {singleError ? <Notice tone="warn" data-single-error="true">{singleError}</Notice> : null}
                {singleResult ? (
                  <Notice tone={Array.isArray(singleResult.failed) && singleResult.failed.length ? 'warn' : 'ok'} data-single-created="true">
                    {singleResult.from_timesheets
                      ? fromTimesheetsSentence(singleResult)
                      : `Paystub #${singleResult.id} created, marked ${singleResult.status || 'pending'}.`}
                  </Notice>
                ) : null}
                <div className="of-inline">
                  <FwButton
                    variant="primary"
                    icon="payroll"
                    onClick={createSingle}
                    loading={busy === 'single'}
                    disabled={!single.user_id || !single.start || !single.end || (!!busy && busy !== 'single')}
                  >
                    Create paystub
                  </FwButton>
                  <FwButton variant="ghost" onClick={() => { setSingleOpen(false); setSingleError(null); }} disabled={!!busy}>Cancel</FwButton>
                </div>
              </div>
            )}
          </section>
        ) : null}

        {isAdmin ? (
          <section className="of-byhand__part" data-panel="generate">
            <SectionLabel>Generate paystubs</SectionLabel>
            <p className="of-muted">One paystub per approved timesheet in the period that does not have one yet. Clock time with no approved sheet is reported, not paid.</p>
            <div className="of-editor__two">
              <FwField label="Period start">
                <FwInput type="date" value={gen.start} onChange={(e) => setGen((g) => ({ ...g, start: e.target.value }))} data-field="pay_period_start" />
              </FwField>
              <FwField label="Period end">
                <FwInput type="date" value={gen.end} onChange={(e) => setGen((g) => ({ ...g, end: e.target.value }))} data-field="pay_period_end" />
              </FwField>
            </div>
            <div className="of-inline">
              <FwButton variant="primary" icon="payroll" onClick={generate} loading={busy === 'generate'} disabled={!gen.start || !gen.end || (!!busy && busy !== 'generate')}>Generate batch</FwButton>
            </div>
            {genResult ? (
              <div className="of-stack" data-generated="true">
                <Notice tone="ok">{genResult.message || `Generated ${(genResult.paystubs || []).length} paystubs`}</Notice>
                {genResult.paystubs && genResult.paystubs.length ? (
                  <ul className="of-list">
                    {genResult.paystubs.map((p) => <li key={p.id}>{p.worker_name}: {formatHours(p.hours)}h, {fmtMoney(p.gross_pay)} gross</li>)}
                  </ul>
                ) : null}
                {unapprovedText ? (
                  <Notice tone="warn" title="Not paid" data-unapproved="true">
                    {unapprovedText} <Link className="of-link" to="/approve">Open Approvals</Link>
                  </Notice>
                ) : null}
              </div>
            ) : null}
          </section>
        ) : null}
          </div>
          </div>
        </details>
      ) : null}

      {openId ? <PaystubSheet id={openId} onClose={() => setOpenId(null)} onChanged={refresh} isAdmin={isAdmin} canManage={canManage} canPdf={canPdf} /> : null}

      {/* ── Make a worker's week ──────────────────────────────────────────
          Two fields, because the server needs exactly two: whose, and which
          week. Everything else — which shifts, how many hours, what they are
          worth — comes from that person's clock, which is the point. A form
          that let somebody TYPE the hours would be a second way for a
          timesheet to exist, and the two would disagree the first time a
          crew member's clock said otherwise. */}
      <Sheet
        open={!!newSheet}
        title="New timesheet"
        onClose={() => { setNewSheet(null); setError(null); }}
        className="of-sheet"
      >
        {newSheet ? (
          <div className="of-editor">
            <p className="of-muted">
              Built from what this person actually clocked that week, then opened for approval.
              If they clocked nothing, there is no sheet to build — the hours have to reach the
              clock first, from their phone or from Dispatch.
            </p>
            <FwField label="Worker" htmlFor="of-new-sheet-worker">
              <FwSelect
                id="of-new-sheet-worker"
                value={newSheet.user_id}
                onChange={(e) => setNewSheet((n) => ({ ...n, user_id: e.target.value }))}
                data-field="new-sheet-worker"
              >
                <option value="">Choose a worker</option>
                {people.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </FwSelect>
            </FwField>
            {/* The WEEK it starts, not a range: routes/timesheets.js
                POST /from-clock takes `week_start` and derives the end itself,
                so offering an end date here would be a second opinion about
                where a week ends. */}
            <FwField label="Week starting" hint="Any date in that week works — the server snaps it to the week it belongs to.">
              <FwInput
                type="date"
                value={newSheet.week_start}
                onChange={(e) => setNewSheet((n) => ({ ...n, week_start: e.target.value }))}
                data-field="new-sheet-week"
              />
            </FwField>
            <div className="of-inline">
              <FwButton
                variant="primary"
                icon="clock"
                onClick={buildSheet}
                loading={busy === 'new-sheet'}
                disabled={!newSheet.user_id || !newSheet.week_start || (!!busy && busy !== 'new-sheet')}
                data-action="build-timesheet"
              >
                Build from their clock
              </FwButton>
              <FwButton variant="ghost" onClick={() => { setNewSheet(null); setError(null); }} disabled={!!busy}>Cancel</FwButton>
            </div>
          </div>
        ) : null}
      </Sheet>
      {/* Named to the PERIOD, because that is what this now closes. The button
          it belongs to said "Mark all pending paid (41)" and meant every
          unpaid paystub in the company, across every period — a straggler
          somebody was deliberately holding back went out with the run and the
          confirm never said which weeks it covered. */}
      <ConfirmSheet
        open={batchOpen}
        title={`Mark ${pendingIds.length} paystub${pendingIds.length === 1 ? '' : 's'} paid?`}
        onClose={() => setBatchOpen(false)}
        onConfirm={batchPaid}
        confirmLabel="Mark paid"
        busy={busy === 'batch'}
        confirmDisabled={!!paidDayProblem(batchDay, companyDay)}
      >
        <PaidDayField
          id="of-batch-paid-on"
          value={batchDay}
          onChange={setBatchDay}
          today={companyDay}
          hint="The day this run went out. It decides which year the pay counts in on the 1099s and Money in & out."
        />
        {period && batchDay && !paidDayProblem(batchDay, companyDay) && batchDay < period.start ? (
          <Notice tone="warn" data-paid-before-period>{`That is before this pay period began (${fmtDate(period.start)}). Check the year.`}</Notice>
        ) : null}
        <p>
          Every unpaid paystub in {periodLabel || 'this pay period'} is marked paid
          {' '}{batchDay && batchDay !== companyDay && !paidDayProblem(batchDay, companyDay) ? `on ${fmtDate(batchDay)}` : 'today'}
          {' '}({pendingIds.length}). Paystubs in any other period are not touched, and ones already
          paid are left alone.
        </p>
      </ConfirmSheet>

      <ConfirmSheet
        open={!!sheetToDelete}
        title={sheetToDelete ? `Delete ${sheetToDelete.user_name || `user #${sheetToDelete.user_id}`}'s timesheet?` : 'Delete timesheet?'}
        onClose={() => { setSheetToDelete(null); setSheetError(null); }}
        onConfirm={deleteSheet}
        confirmLabel="Delete timesheet"
        tone="stop"
        busy={busy === 'delete-sheet'}
      >
        {sheetToDelete ? (
          <div className="of-stack">
            <p>
              Week {fmtDateRange(sheetToDelete.start_date, sheetToDelete.end_date)},
              {' '}{formatHours(sheetToDelete.total_hours)} hours, status {sheetToDelete.status || 'draft'}.
            </p>
            <p>The sheet and every line on it are removed. This cannot be undone.</p>

            {/* What ELSE goes. Three states, and the middle one must never be
                rendered as the third: the route deletes the paystub pointing at
                this sheet whether or not anyone was told, so "we could not find
                out" has to look different from "there is none". */}
            {sheetDetail.isPending ? <p className="of-muted" data-collateral="checking">Checking what else this will delete…</p> : null}
            {sheetDetail.isError ? (
              <Notice tone="stop" data-collateral="unknown">
                Could not check whether a paystub was generated from this sheet. Deleting removes any
                paystub pointing at it — including one already marked paid — so this may destroy a
                payroll record without either of us knowing. Try again before deleting.
              </Notice>
            ) : null}
            {sheetDetail.data ? (
              <Notice
                tone={sheetDetail.data.paystub ? 'stop' : 'info'}
                data-collateral={sheetDetail.data.paystub ? (sheetDetail.data.paystub.status === 'paid' ? 'paid-paystub' : 'paystub') : 'none'}
              >
                {collateralSentence(sheetDetail.data)}
              </Notice>
            ) : null}

            {sheetError ? <Notice tone="stop" data-sheet-error="true">{sheetError}</Notice> : null}
          </div>
        ) : null}
      </ConfirmSheet>
    </>
  );
}
